import React from 'react';
import { Box, Container, Typography, Paper, Grid, TextField, List, ListItem, ListItemText, ListItemButton, Divider, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button } from './ui';

// Helper function to get color based on asset type
const getAssetColor = (rendimento) => {
  switch (rendimento) {
    case 'Renda Fixa':
      return '#2E7D32';
    case 'Renda Variável':
      return '#D32F2F';
    case 'Alternativo':
      return '#8e44ad';
    default:
      return 'secondary.main'; // For "Outros"
  }
};

const SimulatorScreen = ({
  selectedAsset,
  setSelectedAsset,
  investmentValue,
  setInvestmentValue,
  addedAssets,
  setAddedAssets,
  totalInvestment,
  isSelectOpen,
  setIsSelectOpen,
  dropdownData,
  handleAddAsset,
  handleRemoveAsset,
  handleCalculate,
  handleBack
}) => (
  <Box sx={{ 
    minHeight: '100vh', 
    display: 'flex', 
    flexDirection: 'column', 
    justifyContent: 'center',
    // py: 5 
  }}>
    <Container maxWidth="lg">
      <Typography 
        variant="h4" 
        component="h1" 
        sx={{ 
          mb: 4, 
          fontWeight: 600, 
          textAlign: 'center', 
          color: 'white',
          fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' }
        }}
      >
        Simulador Fee Fixo vs Modelo Tradicional
      </Typography>
      <Paper
        sx={{
          width: '100%',
          maxWidth: 650,
          backgroundColor: 'white',
          borderRadius: 4,
          overflow: 'hidden',
          padding: 2,
          position: 'relative',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          mx: 'auto'
        }}
      >
        <Grid container spacing={2}>
          {/* Dropdown and value input */}
          <Grid item xs={12} md={8}>
            <Box sx={{ position: 'relative' }}>
              <Box
                onClick={() => setIsSelectOpen(!isSelectOpen)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#f5f5f5',
                  borderRadius: 3,
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  padding: '8px 14px',
                  fontSize: '1rem',
                  color: selectedAsset ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.6)',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'rgba(0, 0, 0, 0.87)' }
                }}
              >
                <Typography sx={{ fontSize: '1rem' }}>
                  {selectedAsset 
                    ? dropdownData.find(a => a.id.toString() === selectedAsset)?.name || 'Selecione o ativo'
                    : 'Selecione o ativo'}
                </Typography>
                <KeyboardArrowDownIcon 
                  sx={{ 
                    transform: isSelectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                    fontSize: '1.2rem',
                    color: 'rgba(0, 0, 0, 0.54)'
                  }} 
                />
              </Box>
              {isSelectOpen && (
                <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: 'white', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', borderRadius: '8px', maxHeight: 300, overflow: 'auto' }}>
                  <List dense>
                    {dropdownData.map((item) => (
                      <ListItem 
                        key={item.id}
                        disablePadding
                        dense
                        onClick={() => {
                          setSelectedAsset(item.id.toString());
                          setIsSelectOpen(false);
                        }}
                        sx={{ borderLeft: '4px solid', borderLeftColor: getAssetColor(item.rendimento) }}
                      >
                        <ListItemButton sx={{ py: 0.5 }}>
                          <ListItemText 
                            primary={item.name}
                            secondary={item.rendimento}
                            primaryTypographyProps={{ style: { color: '#121C42', fontWeight: 500, fontSize: '0.75rem' } }}
                            secondaryTypographyProps={{ style: { color: getAssetColor(item.rendimento), fontWeight: 500, fontSize: '0.65rem' } }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                size="small"
                label="Digite o valor"
                variant="outlined"
                value={investmentValue}
                onChange={e => setInvestmentValue(e.target.value)}
                type="number"
                sx={{ 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 8,
                  '& .MuiInputLabel-root': { 
                    fontSize: '1rem', 
                    color: 'rgba(0, 0, 0, 0.6)' 
                  }, 
                  '& .MuiOutlinedInput-input': { 
                    fontSize: '1rem', 
                    padding: '8px 14px', 
                    color: 'rgba(0, 0, 0, 0.87)' 
                  }, 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 8,
                    '& fieldset': { 
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      borderRadius: 8
                    }, 
                    '&:hover fieldset': { 
                      borderColor: 'rgba(0, 0, 0, 0.87)' 
                    }
                  } 
                }}
                InputProps={{ style: { color: '#000', borderRadius: 8 } }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Button
              buttonType="add"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAddAsset}
              sx={{ borderRadius: 8 }}
            >
              Adicionar
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ backgroundColor: '#f5f5f5', borderRadius: 3, height: 200, overflow: 'auto', p: 1 }}>
              {addedAssets.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1, fontSize: '0.85rem', textAlign: 'center', marginTop: 7 }}>
                  Itens adicionados aparecerão aqui
                </Typography>
              ) : (
                <List>
                  {addedAssets.map((asset) => (
                    <React.Fragment key={asset.id}>
                      <ListItem 
                        sx={{ py: 0.5 }}
                        secondaryAction={
                          <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveAsset(asset.id)} size="small" sx={{ color: '#D32F2F' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Grid container>
                          <Grid item xs={7}>
                            <ListItemText 
                              primary={asset.name} 
                              secondary={asset.rendimento}
                              primaryTypographyProps={{ style: { color: '#121C42', fontWeight: 500, fontSize: '0.85rem' } }}
                              secondaryTypographyProps={{ style: { color: getAssetColor(asset.rendimento), fontWeight: 500, fontSize: '0.75rem' } }}
                            />
                          </Grid>
                          <Grid item xs={4} sx={{ textAlign: 'right', alignContent: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#121C42', fontSize: '0.85rem' }}>
                              R${asset.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Grid>
                        </Grid>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Button
              buttonType="calculate"
              fullWidth
              onClick={handleCalculate}
              disabled={addedAssets.length === 0}
              sx={{ borderRadius: 8 }}
            >
              Calcular
            </Button>
          </Grid>
          <Grid 
            container 
            sx={{ mt: 2, px: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 1, sm: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FiberManualRecordIcon sx={{ color: '#2E7D32', fontSize: 12, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#333' }}>
                  RENDA FIXA
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FiberManualRecordIcon sx={{ color: '#D32F2F', fontSize: 12, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#333' }} md={{fontSize: '0.7rem'}}>
                  RENDA VARIÁVEL
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FiberManualRecordIcon sx={{ color: 'secondary.main', fontSize: 12, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#333' }} md={{fontSize: '0.7rem'}}>
                  OUTROS
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FiberManualRecordIcon sx={{ color: '#8e44ad', fontSize: 12, mr: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#333' }} md={{fontSize: '0.7rem'}}>
                  ALTERNATIVO
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{ py: 0.8, px: 2, backgroundColor: 'secondary.main', color: 'black', borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 180 }}
            >
              Total investido: {totalInvestment > 0 
                ? `R$ ${totalInvestment.toLocaleString('pt-BR')}`
                : "R$ 0,00"}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  </Box>
);

export default SimulatorScreen; 